/**
 * 🏦 Bank Verification Service
 * Handles admin endpoints for bank account NIN verification management
 */

import apiClient from './apiClient';

export interface BankAccount {
  id: string;
  accountNumber: string;
  accountName: string;
  bankName: string;
  bankCode: string;
  bankCountry: string;
  isVerified: boolean;
  isPrimary: boolean;
  isNinVerified: boolean;
  requiresManualApproval: boolean;
  approvalStatus: 'approved' | 'pending_review' | 'rejected';
  mismatchAttempts: number;
  createdAt: string;

  user: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    phone: string;
    verificationDocumentType: 'nin' | 'passport' | null;
    ninLegalName: string | null;
    country: string;
  };

  adminOverride: {
    overriddenBy: string;
    overriddenByEmail: string;
    overriddenAt: string;
    action: 'approve' | 'reject';
    reason: string;
    justification: string;
  } | null;
}

export interface BankVerificationSummary {
  totalAccounts: number;
  approvedCount: number;
  pendingCount: number;
  rejectedCount: number;
  ninVerifiedCount: number;
  manualApprovalCount: number;
  passportUsers: number;
  accountsWithMismatches: number;
}

export interface BankVerificationFilters {
  page?: number;
  limit?: number;
  status?: 'approved' | 'pending_review' | 'rejected' | 'all';
  search?: string;
}

export interface AuditLog {
  id: string;
  action: 'nin_check' | 'admin_override' | 'country_check' | 'passport_verification';
  status: 'success' | 'failure' | 'pending' | 'rejected';
  createdAt: string;

  user: {
    id: string;
    fullName: string;
    email: string;
  };

  bankAccount: {
    id: string;
    accountNumber: string;
    accountName: string;
    bankName: string;
  };

  ninCorrelation: {
    ninLegalName: string;
    bankAccountName: string;
    similarityScore: number;
  } | null;

  countryValidation: any | null;

  adminOverride: {
    adminId: string;
    adminEmail: string;
    adminName: string;
    reason: string;
    justification: string;
    timestamp: string;
  } | null;

  error: {
    code: string;
    message: string;
  } | null;

  metadata: {
    ipAddress: string;
    userAgent: string;
  };
}

export interface AuditLogSummary {
  totalLogs: number;
  ninChecksPassed: number;
  ninChecksFailed: number;
  adminOverrides: number;
  countryMismatches: number;
  passportVerifications: number;
  uniqueUsers: number;
}

export interface AuditLogFilters {
  page?: number;
  limit?: number;
  action?: 'nin_check' | 'admin_override' | 'country_check' | 'passport_verification';
  status?: 'success' | 'failure' | 'pending' | 'rejected';
  user_id?: string;
}

export interface CountryBank {
  id: string;
  country: string;
  bankCode: string;
  bankName: string;
  isActive: boolean;
  addedAt: string;
  addedBy: string;
  attempts: number; // Number of times users tried to add this bank
}

export const bankVerificationService = {
  /**
   * Get all bank accounts with NIN verification status
   */
  async getBankAccounts(filters?: BankVerificationFilters): Promise<{
    success: boolean;
    data?: {
      summary: BankVerificationSummary;
      bankAccounts: BankAccount[];
      pagination: {
        page: number;
        limit: number;
        totalCount: number;
        totalPages: number;
      };
    };
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.page) queryParams.append('page', filters.page.toString());
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.status && filters.status !== 'all') queryParams.append('status', filters.status);
      if (filters?.search) queryParams.append('search', filters.search);
      if (filters?.startDate) queryParams.append('startDate', filters.startDate);
      if (filters?.endDate) queryParams.append('endDate', filters.endDate);

      const url = `/api/v1/admin/bank-verification/list${queryParams.toString() ? `?${queryParams}` : ''}`;


      const response = await apiClient.get(url);


      if (response.data.success) {
        // Ensure we have valid data structure
        const accounts = response.data.data?.accounts || [];
        const summary = response.data.data?.summary || {
          totalAccounts: 0,
          approvedCount: 0,
          pendingCount: 0,
          rejectedCount: 0,
          ninVerifiedCount: 0,
          manualApprovalCount: 0,
          passportUsers: 0,
          accountsWithMismatches: 0,
        };
        const pagination = response.data.data?.pagination || {
          page: 1,
          limit: 50,
          totalCount: 0,
          totalPages: 0,
        };

        return {
          success: true,
          data: {
            summary,
            bankAccounts: accounts, // Backend uses "accounts" not "bankAccounts"
            pagination,
          }
        };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to fetch bank accounts'
        };
      }
    } catch (error: any) {
      console.error('❌ Error fetching bank accounts:', error);
      let errorMessage = 'Failed to fetch bank accounts';

      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  },

  /**
   * Override NIN verification for a bank account (approve or reject)
   */
  async overrideVerification(
    bankAccountId: string,
    data: {
      action: 'approve' | 'reject';
      reason: 'legal_name_change' | 'passport_verification' | 'name_variation' | 'other';
      justification: string;
    }
  ): Promise<{
    success: boolean;
    data?: {
      accountId: string;
      action: string;
      newStatus: string;
      approvedBy: {
        adminId: string;
        adminEmail: string;
        adminName: string;
      };
      reason: string;
      timestamp: string;
      account: {
        accountNumber: string;
        accountName: string;
        bankName: string;
        user: {
          email: string;
          name: string;
        };
      };
    };
    error?: string;
  }> {
    try {

      const response = await apiClient.post(
        `/api/v1/admin/bank-verification/${bankAccountId}/override`,
        data
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          error: response.data.error?.message || `Failed to ${data.action} account`
        };
      }
    } catch (error: any) {
      console.error(`❌ Error ${data.action}ing account:`, error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || `Failed to ${data.action} account`
      };
    }
  },

  /**
   * Get audit logs for bank verification
   */
  async getAuditLogs(filters?: AuditLogFilters): Promise<{
    success: boolean;
    data?: {
      summary: AuditLogSummary;
      logs: AuditLog[];
      pagination: {
        page: number;
        limit: number;
        totalCount: number;
        totalPages: number;
      };
    };
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.page) queryParams.append('page', filters.page.toString());
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.action) queryParams.append('action', filters.action);
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.user_id) queryParams.append('user_id', filters.user_id);

      const url = `/api/v1/admin/bank-verification/audit-logs${queryParams.toString() ? `?${queryParams}` : ''}`;


      const response = await apiClient.get(url);

      if (response.data.success) {
        return {
          success: true,
          data: {
            summary: response.data.data.summary,
            logs: response.data.data.logs,
            pagination: response.data.data.pagination,
          }
        };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to fetch audit logs'
        };
      }
    } catch (error: any) {
      console.error('❌ Error fetching audit logs:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to fetch audit logs'
      };
    }
  },

  /**
   * Export bank verification data to CSV
   */
  async exportToCSV(filters?: BankVerificationFilters): Promise<Blob> {
    const queryParams = new URLSearchParams();

    if (filters?.status && filters.status !== 'all') queryParams.append('status', filters.status);
    if (filters?.search) queryParams.append('search', filters.search);
    if (filters?.startDate) queryParams.append('startDate', filters.startDate);
    if (filters?.endDate) queryParams.append('endDate', filters.endDate);

    const url = `/api/v1/admin/bank-verification/export${queryParams.toString() ? `?${queryParams}` : ''}`;

    const response = await apiClient.get(url, {
      responseType: 'blob'
    });

    return response.data;
  },

  /**
   * Approve a manually pending bank account (for passport users)
   */
  async approveAccount(
    bankAccountId: string,
    notifyUser: boolean = true
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const response = await apiClient.post(
        `/api/v1/admin/bank-verification/${bankAccountId}/approve`,
        { notifyUser }
      );

      if (response.data.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to approve account'
        };
      }
    } catch (error: any) {
      console.error('❌ Error approving account:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to approve account'
      };
    }
  },

  /**
   * Reject a bank account with reason
   */
  async rejectAccount(
    bankAccountId: string,
    data: {
      reason: string;
      notifyUser: boolean;
    }
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const response = await apiClient.post(
        `/api/v1/admin/bank-verification/${bankAccountId}/reject`,
        data
      );

      if (response.data.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to reject account'
        };
      }
    } catch (error: any) {
      console.error('❌ Error rejecting account:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to reject account'
      };
    }
  },

  /**
   * Request more information from user
   */
  async requestMoreInfo(
    bankAccountId: string,
    data: {
      message: string;
      documentsNeeded: string[];
    }
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const response = await apiClient.post(
        `/api/v1/admin/bank-verification/${bankAccountId}/request-info`,
        data
      );

      if (response.data.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to request more info'
        };
      }
    } catch (error: any) {
      console.error('❌ Error requesting more info:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to request more info'
      };
    }
  },

  /**
   * Get country-bank validation settings
   */
  async getCountryBanks(country?: string): Promise<{
    success: boolean;
    data?: {
      banks: CountryBank[];
    };
    error?: string;
  }> {
    try {
      const url = `/api/v1/admin/bank-verification/country-banks${country ? `?country=${country}` : ''}`;

      const response = await apiClient.get(url);

      if (response.data.success) {
        return {
          success: true,
          data: {
            banks: response.data.data.banks,
          }
        };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to fetch country banks'
        };
      }
    } catch (error: any) {
      console.error('❌ Error fetching country banks:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to fetch country banks'
      };
    }
  },

  /**
   * Add a bank to country's allowed list
   */
  async addCountryBank(data: {
    country: string;
    bankCode: string;
    bankName: string;
  }): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const response = await apiClient.post('/api/v1/admin/bank-verification/country-banks', data);

      if (response.data.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to add bank'
        };
      }
    } catch (error: any) {
      console.error('❌ Error adding bank:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to add bank'
      };
    }
  },

  /**
   * Toggle bank active status
   */
  async toggleBankStatus(bankId: string, isActive: boolean): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const response = await apiClient.patch(
        `/api/v1/admin/bank-verification/country-banks/${bankId}`,
        { isActive }
      );

      if (response.data.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to update bank status'
        };
      }
    } catch (error: any) {
      console.error('❌ Error updating bank status:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to update bank status'
      };
    }
  },

  /**
   * Export audit logs to CSV
   */
  async exportAuditLogs(filters?: {
    startDate?: string;
    endDate?: string;
    userId?: string;
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();

    if (filters?.startDate) queryParams.append('startDate', filters.startDate);
    if (filters?.endDate) queryParams.append('endDate', filters.endDate);
    if (filters?.userId) queryParams.append('userId', filters.userId);

    const url = `/api/v1/admin/bank-verification/audit-logs/export${queryParams.toString() ? `?${queryParams}` : ''}`;

    const response = await apiClient.get(url, {
      responseType: 'blob'
    });

    return response.data;
  }
};
